import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DateTime } from 'luxon';

export type AvailabilityKey = 'in_stock' | 'sold' | 'restoration' | 'reserved';
export type ApprovalKey = 'approved' | 'pending' | 'rejected' | 'draft';

const AVAILABILITY_CLASSES: Record<AvailabilityKey, string> = {
  in_stock: 'bg-green-600 text-white',
  sold: 'bg-[#dc2626] text-white',
  restoration: 'bg-[#f97316] text-white',
  reserved: 'bg-[#1e1e14] text-[#d4b86a] border border-[#d4b86a]',
};

const APPROVAL_CLASSES: Record<ApprovalKey, string> = {
  approved: 'bg-green-600 text-white',
  pending: 'bg-amber-500 text-white',
  rejected: 'bg-red-motorbest-600 text-white',
  draft: 'bg-slate-400 text-white',
};

/**
 * Card de anúncio para o dashboard do seller (classifies + automobilia).
 *
 * Não contém lógica de negócio (créditos, confirmações) — só apresenta o
 * `product` e emite eventos; quem decide o que fazer é o list.component pai.
 */
@Component({
  selector: 'app-seller-listing-card',
  templateUrl: './seller-listing-card.component.html',
  styleUrls: ['./seller-listing-card.component.scss'],
})
export class SellerListingCardComponent {
  @Input() product: any;
  @Input() seller: any;
  @Input() canToggleVisibility = false;

  @Output() edit: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleVisibility: EventEmitter<void> = new EventEmitter<void>();
  @Output() delete: EventEmitter<void> = new EventEmitter<void>();

  get attrs(): any {
    return this.product?.attributes ?? {};
  }

  get imageUrl(): string | null {
    return this.attrs.images?.data?.[0]?.attributes?.url ?? null;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ State pills
  // -----------------------------------------------------------------------------------------------------

  get availabilityKey(): AvailabilityKey {
    return this.attrs.availability ?? 'in_stock';
  }

  get availabilityClasses(): string {
    return (
      AVAILABILITY_CLASSES[this.availabilityKey] ??
      AVAILABILITY_CLASSES.in_stock
    );
  }

  get approvalKey(): ApprovalKey {
    if (this.attrs.rejected) {
      return 'rejected';
    }
    if (this.attrs.approved) {
      return 'approved';
    }
    if (this.attrs.submitted) {
      return 'pending';
    }
    return 'draft';
  }

  get approvalClasses(): string {
    return APPROVAL_CLASSES[this.approvalKey];
  }

  get isVisible(): boolean {
    return this.attrs.client_visivel_status_selected ?? true;
  }

  /** Dias até expirar (negativo se já expirou). Null se o anúncio não tiver data de expiração. */
  get expiryDays(): number | null {
    if (!this.attrs.expiresAt) {
      return null;
    }
    const diff = DateTime.fromISO(this.attrs.expiresAt)
      .startOf('day')
      .diff(DateTime.now().startOf('day'), 'days').days;
    return Math.round(diff);
  }

  get isNearExpiry(): boolean {
    return (
      this.expiryDays !== null && this.expiryDays >= 0 && this.expiryDays <= 7
    );
  }

  get isExpired(): boolean {
    return this.expiryDays !== null && this.expiryDays < 0;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Meta / classification
  // -----------------------------------------------------------------------------------------------------

  get metaLine(): string {
    const brand =
      this.attrs.product_brand?.data?.attributes?.name ||
      this.attrs.customvalue_brand;
    const model =
      this.attrs.product_model?.data?.attributes?.name ||
      this.attrs.customvalue_model;
    const city =
      this.attrs.city?.data?.attributes?.name || this.attrs.city_custom;

    return [brand, model, this.attrs.year, city].filter(Boolean).join(' · ');
  }

  get priceLabel(): number | null {
    return this.attrs.price || null;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Adaptativo ao tipo de conta (isClub / dealerActive vêm do seller, não do produto)
  // -----------------------------------------------------------------------------------------------------

  get isClubAccount(): boolean {
    return !!this.seller?.isClub;
  }

  get isDealerAccount(): boolean {
    return !!this.seller?.dealerActive;
  }

  get memberLabel(): string | null {
    if (!this.isClubAccount || !this.attrs.socioName) {
      return null;
    }
    return this.attrs.nSocio
      ? `${this.attrs.socioName} (#${this.attrs.nSocio})`
      : this.attrs.socioName;
  }

  /** Publicado no marketplace público (aprovado + visível). */
  get onMarketplace(): boolean {
    return !!this.attrs.approved && this.isVisible;
  }

  /**
   * Site do dealer mostra todos os anúncios aprovados, independentemente do
   * toggle de visibilidade do marketplace.
   */
  get onDealerSite(): boolean {
    return this.isDealerAccount && !!this.attrs.approved;
  }

  get onClub(): boolean {
    return this.isClubAccount && this.onMarketplace;
  }
}
